/**
 * Admin Authentication Hook
 * Extracts admin permissions from Kinde JWT and provides permission checks
 * 
 * Admin access is determined by having any permissions that start with "admin:"
 * No specific role (like ADMIN) is required - only the presence of admin permissions.
 * 
 * Usage:
 * const { isAdmin, canManageUsersAndResources, kindeId } = useAdminAuth();
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '@/features/authentication/hooks/useAuth';
import type { UserRole } from '@/core/auth/types';
import type { AdminPermission } from '../types/admin-permissions.type';

type UseAdminAuthReturn = {
  isAdmin: boolean;
  isLoading: boolean;
  permissions: AdminPermission[];
  canManageUsersAndResources: boolean;
  canViewBasicMetrics: boolean;
  canViewAllMetrics: boolean;
  canOverrideVettingCheck: boolean;
  kindeId: string | null;
  email: string | null;
  userId?: string | null;
  role?: UserRole | null;
};

/**
 * Decode JWT token to extract permissions
 * Permissions are stored in the 'permissions' claim as an array
 */
function decodeJwtToken(token: string): { permissions?: string[] } | null {
  try {
    const base64Url = token.split('.')[1];
    if (!base64Url) return null;
    
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Failed to decode JWT token:', error);
    return null;
  }
}

export function useAdminAuth(): UseAdminAuthReturn {
  const { user, isAuthenticated, isLoading, getAccessToken } = useAuth();
  const [permissions, setPermissions] = useState<AdminPermission[]>([]);
  const [tokenLoading, setTokenLoading] = useState(true);

  // Extract permissions from JWT token for all authenticated users
  const extractPermissions = useCallback(async () => {
    if (!isAuthenticated || isLoading) {
      setPermissions([]);
      setTokenLoading(false);
      return;
    }

    try {
      const token = await getAccessToken();
      if (!token) {
        setPermissions([]);
        setTokenLoading(false);
        return;
      }

      const decoded = decodeJwtToken(token);
      if (decoded?.permissions && Array.isArray(decoded.permissions)) {
        // Filter only admin-specific permissions
        const adminPermissions = decoded.permissions.filter(
          (p): p is AdminPermission => 
            p.startsWith('admin:')
        );
        setPermissions(adminPermissions);
      } else {
        setPermissions([]);
      }
    } catch (error) {
      console.error('Failed to extract admin permissions:', error);
      setPermissions([]);
    } finally {
      setTokenLoading(false);
    }
  }, [isAuthenticated, isLoading, getAccessToken]);

  useEffect(() => {
    extractPermissions();
  }, [extractPermissions]);

  // Check if user is admin (has any admin permissions starting with "admin:")
  const isAdmin = useMemo(() => {
    return isAuthenticated && permissions.length > 0;
  }, [isAuthenticated, permissions]);

  // Permission checks
  const canManageUsersAndResources = useMemo(() => {
    return permissions.includes('admin:manage-users-and-resources');
  }, [permissions]);

  const canViewBasicMetrics = useMemo(() => {
    return permissions.includes('admin:view-basic-metrics');
  }, [permissions]);

  const canViewAllMetrics = useMemo(() => {
    return permissions.includes('admin:view-all-metrics');
  }, [permissions]);

  // Feature 099 — assigned to the "adjudicator" role in Kinde (confirmed 17 Sep 2026).
  const canOverrideVettingCheck = useMemo(() => {
    return permissions.includes('admin:manage-user-background-vetting');
  }, [permissions]);

  return {
    isAdmin,
    isLoading: isLoading || tokenLoading,
    permissions,
    canManageUsersAndResources,
    canViewBasicMetrics,
    canViewAllMetrics,
    canOverrideVettingCheck,
    kindeId: user?.kindeId || null,
    email: user?.email || null,
    userId: user?.id || null,
    role: user?.role || null,
  };
}
